import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle, Clock, Upload, Plus, FileText,
  ChevronDown, ChevronUp, Download, X, AlertCircle, Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  savePCAReport, getPCAReports,
  savePCAItem, getPCAItems,
  updatePCAItem, uploadPCAEvidence,
} from '../firebaseB2BService';

// ── Tenant complaint category → PCA system category mapping ──────────────────
const TENANT_TO_PCA = {
  'Heating/Cooling': ['HVAC', 'Mechanical', 'Boiler'],
  'Plumbing':        ['Plumbing', 'Domestic Water', 'Sewer'],
  'Moisture':        ['Roofing', 'Waterproofing', 'Drainage'],
  'Mold':            ['Roofing', 'Waterproofing', 'Ventilation'],
  'Structural Issue':['Structural', 'Foundation', 'Masonry'],
  'Safety Issue':    ['Life Safety', 'Fire Protection', 'Egress'],
  'Pest Issue':      ['Building Envelope', 'Sealing'],
  'Maintenance':     ['General', 'Site'],
  'Trip Hazard':     ['Site', 'Paving'],
  'Electrical':      ['Electrical'],
  'Noise':           ['Building Envelope', 'Windows'],
};

const PCA_CATEGORIES = [
  'HVAC', 'Roofing', 'Plumbing', 'Electrical', 'Structural',
  'Life Safety', 'Building Envelope', 'Site', 'Elevator',
  'Waterproofing', 'Foundation', 'Mechanical', 'General'
];

const REPORT_TYPES = [
  { key: 'PCA',      label: 'Property Condition Assessment (PCA)' },
  { key: 'Phase I',  label: 'Environmental Site Assessment — Phase I' },
  { key: 'Phase II', label: 'Environmental Site Assessment — Phase II' },
  { key: 'Appraisal',label: 'Appraisal' },
  { key: 'Zoning',   label: 'Zoning Report' },
  { key: 'ALTA',     label: 'ALTA Survey' },
];

// Returns which tenant categories flag against a PCA item's category
const getTenantSignals = (pcaCategory, tenantReviews = []) => {
  const matchingTenantCategories = Object.entries(TENANT_TO_PCA)
    .filter(([, pcaCats]) => pcaCats.includes(pcaCategory))
    .map(([tenantCat]) => tenantCat);

  return tenantReviews.filter(r => matchingTenantCategories.includes(r.category));
};

const fmtCurrency = (n) =>
  n ? `$${Number(n).toLocaleString()}` : '—';

const fmtDate = (ts) => {
  if (!ts) return '—';
  if (ts.toDate) return ts.toDate().toLocaleDateString();
  return new Date(ts).toLocaleDateString();
};

const StatusBadge = ({ status }) => {
  const map = {
    open:        { label: 'Open',        bg: '#FEF2F2', color: '#DC2626', icon: <AlertCircle size={12} /> },
    in_progress: { label: 'In Progress', bg: '#FFFBEB', color: '#D97706', icon: <Clock size={12} /> },
    complete:    { label: 'Complete',    bg: '#ECFDF5', color: '#059669', icon: <CheckCircle size={12} /> },
  };
  const s = map[status] || map.open;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      backgroundColor: s.bg, color: s.color,
      padding: '0.2rem 0.6rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: '600'
    }}>
      {s.icon} {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function PCATracker({ building, user, accountType, demoMode, colors }) {
  const [activeTab, setActiveTab] = useState('immediate');
  const [reports, setReports]     = useState([]);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);

  // Upload report state
  const [showAddReport, setShowAddReport]         = useState(false);
  const [reportType, setReportType]               = useState('PCA');
  const [reportFirm, setReportFirm]               = useState('');
  const [reportDate, setReportDate]               = useState('');
  const [lenderName, setLenderName]               = useState('');
  const [loanAmount, setLoanAmount]               = useState('');
  const [originationDate, setOriginationDate]     = useState('');
  const [maturityDate, setMaturityDate]           = useState('');
  const [reportFile, setReportFile]               = useState(null);
  const [uploadingReport, setUploadingReport]     = useState(false);

  // Add item state
  const [showAddItem, setShowAddItem]             = useState(false);
  const [newItemPriority, setNewItemPriority]     = useState('immediate');
  const [newItemCategory, setNewItemCategory]     = useState('HVAC');
  const [newItemDesc, setNewItemDesc]             = useState('');
  const [newItemCost, setNewItemCost]             = useState('');
  const [newItemDue, setNewItemDue]               = useState('');
  const [newItemYear, setNewItemYear]             = useState('');
  const [savingItem, setSavingItem]               = useState(false);
  const [linkedReportId, setLinkedReportId]       = useState('');

  // Evidence upload state
  const [expandedItem, setExpandedItem]           = useState(null);
  const [evidenceNotes, setEvidenceNotes]         = useState('');
  const [evidenceFile, setEvidenceFile]           = useState(null);
  const [savingEvidence, setSavingEvidence]       = useState(false);

  useEffect(() => {
    if (building?.address) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building?.address]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([
        getPCAReports(building.address),
        getPCAItems(building.address),
      ]);
      setReports(r);
      setItems(i);
    } catch (e) {
      toast.error('Failed to load PCA data');
    } finally {
      setLoading(false);
    }
  };

  // ── Upload a PCA / third-party report PDF ───────────────────────────────
  const handleUploadReport = async () => {
    if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
    if (!reportFile) { toast.error('Please select a file'); return; }
    if (!reportDate) { toast.error('Please enter the report date'); return; }

    setUploadingReport(true);
    try {
      await savePCAReport(building.address, reportFile, {
        reportType, reportFirm, reportDate,
        loanInfo: { lenderName, loanAmount: Number(loanAmount), originationDate, maturityDate },
      }, user.uid, accountType);
      toast.success('Report uploaded');
      setShowAddReport(false);
      setReportFile(null); setReportFirm(''); setReportDate('');
      setLenderName(''); setLoanAmount(''); setOriginationDate(''); setMaturityDate('');
      await loadAll();
    } catch (e) {
      toast.error('Upload failed: ' + e.message);
    } finally {
      setUploadingReport(false);
    }
  };

  // ── Save a new PCA line item ──────────────────────────────────────────────
  const handleSaveItem = async () => {
    if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
    if (!newItemDesc.trim()) { toast.error('Please enter a description'); return; }
    if (newItemPriority === 'capital_reserve' && !newItemYear) { toast.error('Please enter the year due'); return; }

    setSavingItem(true);
    try {
      await savePCAItem(building.address, {
        pcaReportId: linkedReportId || null,
        priority: newItemPriority,
        category: newItemCategory,
        description: newItemDesc,
        estimatedCost: Number(newItemCost) || 0,
        dueDate: newItemPriority !== 'capital_reserve' ? newItemDue : null,
        yearDue: newItemPriority === 'capital_reserve' ? Number(newItemYear) : null,
        status: 'open',
      }, user.uid);
      toast.success('Item added');
      setShowAddItem(false);
      setNewItemDesc(''); setNewItemCost(''); setNewItemDue(''); setNewItemYear('');
      setLinkedReportId('');
      await loadAll();
    } catch (e) {
      toast.error('Failed to save item: ' + e.message);
    } finally {
      setSavingItem(false);
    }
  };

  // ── Mark complete / in-progress ──────────────────────────────────────────
  const handleStatusChange = async (itemId, newStatus) => {
    if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
    try {
      await updatePCAItem(itemId, { status: newStatus });
      toast.success(newStatus === 'complete' ? 'Marked complete ✓' : 'Status updated');
      await loadAll();
    } catch (e) {
      toast.error('Failed to update: ' + e.message);
    }
  };

  // ── Upload evidence against a PCA item ───────────────────────────────────
  const handleEvidenceUpload = async (itemId) => {
    if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
    if (!evidenceFile && !evidenceNotes.trim()) {
      toast.error('Please add a file or notes');
      return;
    }
    setSavingEvidence(true);
    try {
      let fileUrl = null;
      if (evidenceFile) {
        fileUrl = await uploadPCAEvidence(itemId, evidenceFile, user.uid);
      }
      await updatePCAItem(itemId, {
        status: 'in_progress',
        evidenceNotes: evidenceNotes,
        ...(fileUrl ? { evidenceUrls: [fileUrl] } : {}),
      });
      toast.success('Evidence saved');
      setEvidenceFile(null);
      setEvidenceNotes('');
      setExpandedItem(null);
      await loadAll();
    } catch (e) {
      toast.error('Failed to save evidence: ' + e.message);
    } finally {
      setSavingEvidence(false);
    }
  };

  const immediateItems    = items.filter(i => i.priority !== 'capital_reserve');
  const reserveItems      = items.filter(i => i.priority === 'capital_reserve');
  const openImmediateCount = immediateItems.filter(i => i.status !== 'complete').length;
  const totalCostOpen     = immediateItems
    .filter(i => i.status !== 'complete')
    .reduce((s, i) => s + (i.estimatedCost || 0), 0);

  // ── Styling helpers ───────────────────────────────────────────────────────
  const tabStyle = (key) => ({
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: activeTab === key ? '700' : '500',
    color: activeTab === key ? colors.primary : '#64748b',
    borderBottom: activeTab === key ? `2px solid ${colors.primary}` : '2px solid transparent',
    background: 'none', border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: activeTab === key ? colors.primary : 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.75rem', fontSize: '0.875rem',
    border: '1px solid #e2e8f0', borderRadius: '0.375rem',
    outline: 'none', boxSizing: 'border-box',
  };

  const btnPrimary = {
    padding: '0.5rem 1rem', backgroundColor: colors.primary, color: 'white',
    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
    fontSize: '0.875rem', fontWeight: '600',
  };

  const btnSecondary = {
    padding: '0.5rem 1rem', backgroundColor: 'white', color: '#475569',
    border: '1px solid #e2e8f0', borderRadius: '0.375rem', cursor: 'pointer',
    fontSize: '0.875rem', fontWeight: '500',
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
        Loading PCA data...
      </div>
    );
  }

  return (
    <div>
      {/* ── Summary bar ──────────────────────────────────────────────────── */}
      {openImmediateCount > 0 && (
        <div style={{
          display: 'flex', gap: '1rem', padding: '1rem',
          backgroundColor: '#FEF2F2', borderRadius: '0.5rem',
          border: '1px solid #FECACA', marginBottom: '1.5rem',
          flexWrap: 'wrap', alignItems: 'center',
        }}>
          <AlertTriangle size={18} color="#DC2626" />
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#DC2626' }}>
            {openImmediateCount} open repair item{openImmediateCount !== 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '0.875rem', color: '#7F1D1D' }}>
            {fmtCurrency(totalCostOpen)} outstanding
          </span>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem', overflowX: 'auto' }}>
        <button style={tabStyle('immediate')} onClick={() => setActiveTab('immediate')}>
          Immediate Repairs {openImmediateCount > 0 && (
            <span style={{ marginLeft: '0.35rem', backgroundColor: '#DC2626', color: 'white', borderRadius: '9999px', padding: '0 0.4rem', fontSize: '0.7rem' }}>
              {openImmediateCount}
            </span>
          )}
        </button>
        <button style={tabStyle('reserves')} onClick={() => setActiveTab('reserves')}>
          Capital Reserves ({reserveItems.length})
        </button>
        <button style={tabStyle('reports')} onClick={() => setActiveTab('reports')}>
          Reports on File ({reports.length})
        </button>
        <button style={tabStyle('signals')} onClick={() => setActiveTab('signals')}>
          Tenant Signals
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* IMMEDIATE REPAIRS TAB                                             */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'immediate' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Immediate Repairs & Short-Term Needs
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.2rem 0 0' }}>
                Items from PCA report requiring action within 90 days – 1 year
              </p>
            </div>
            {accountType !== 'lender' && (
              <button style={btnPrimary} onClick={() => setShowAddItem(true)}>
                <Plus size={14} style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                Add Item
              </button>
            )}
          </div>

          {/* Add Item Form */}
          {showAddItem && (
            <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.5rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.925rem', fontWeight: '700' }}>New Repair Item</h4>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowAddItem(false)}>
                  <X size={16} color="#94a3b8" />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Priority</label>
                  <select style={inputStyle} value={newItemPriority} onChange={e => setNewItemPriority(e.target.value)}>
                    <option value="immediate">Immediate (0–90 days)</option>
                    <option value="short_term">Short Term (1 year)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>System Category</label>
                  <select style={inputStyle} value={newItemCategory} onChange={e => setNewItemCategory(e.target.value)}>
                    {PCA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Description *</label>
                  <input style={inputStyle} value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} placeholder="e.g. Replace roof membrane Section B" />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Estimated Cost ($)</label>
                  <input style={inputStyle} type="number" value={newItemCost} onChange={e => setNewItemCost(e.target.value)} placeholder="18500" />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Due Date</label>
                  <input style={inputStyle} type="date" value={newItemDue} onChange={e => setNewItemDue(e.target.value)} />
                </div>
                {reports.filter(r => r.reportType === 'PCA').length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Link to PCA Report (optional)</label>
                    <select style={inputStyle} value={linkedReportId} onChange={e => setLinkedReportId(e.target.value)}>
                      <option value="">— Not linked —</option>
                      {reports.filter(r => r.reportType === 'PCA').map(r => (
                        <option key={r.id} value={r.id}>{r.reportFirm || 'PCA'} — {r.reportDate}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button style={btnSecondary} onClick={() => setShowAddItem(false)}>Cancel</button>
                <button style={btnPrimary} onClick={handleSaveItem} disabled={savingItem}>
                  {savingItem ? 'Saving...' : 'Save Item'}
                </button>
              </div>
            </div>
          )}

          {/* Items List */}
          {immediateItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <FileText size={32} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
              <p style={{ margin: 0, fontWeight: '500' }}>No repair items yet</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
                {accountType !== 'lender' ? 'Add items from your PCA report above' : 'PM will add items from their PCA report'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {immediateItems.map(item => {
                const signals = getTenantSignals(item.category, building.reviews || []);
                const isExpanded = expandedItem === item.id;
                return (
                  <div key={item.id} style={{
                    border: `1px solid ${signals.length > 0 && item.status !== 'complete' ? '#FECACA' : '#E2E8F0'}`,
                    borderRadius: '0.5rem', overflow: 'hidden',
                    backgroundColor: item.status === 'complete' ? '#F8FFF8' : 'white',
                  }}>
                    {/* Item header */}
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                          <StatusBadge status={item.status} />
                          <span style={{ fontSize: '0.75rem', backgroundColor: '#F1F5F9', color: '#475569', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontWeight: '500' }}>
                            {item.category}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {item.priority === 'immediate' ? '⚡ Immediate' : '📅 Short Term'}
                          </span>
                          {signals.length > 0 && item.status !== 'complete' && (
                            <span style={{ fontSize: '0.75rem', backgroundColor: '#FEF2F2', color: '#DC2626', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                              <Zap size={10} /> {signals.length} tenant signal{signals.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem', fontWeight: '600', color: '#0f172a' }}>
                          {item.description}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
                          {item.estimatedCost > 0 && <span>{fmtCurrency(item.estimatedCost)}</span>}
                          {item.dueDate && <span>Due {new Date(item.dueDate).toLocaleDateString()}</span>}
                          {item.completedAt && <span>Completed {fmtDate(item.completedAt)}</span>}
                        </div>
                        {item.evidenceNotes && (
                          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
                            "{item.evidenceNotes}"
                          </p>
                        )}
                        {item.evidenceUrls?.length > 0 && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {item.evidenceUrls.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '0.75rem', color: colors.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                <Download size={11} /> Evidence {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                        {item.status !== 'complete' && (
                          <>
                            <button
                              style={{ ...btnPrimary, padding: '0.35rem 0.75rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => handleStatusChange(item.id, 'complete')}
                            >
                              <CheckCircle size={12} /> Complete
                            </button>
                            <button
                              style={{ ...btnSecondary, padding: '0.35rem 0.75rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                            >
                              <Upload size={12} /> Evidence
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </button>
                          </>
                        )}
                        {item.status === 'complete' && (
                          <button
                            style={{ ...btnSecondary, padding: '0.35rem 0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}
                            onClick={() => handleStatusChange(item.id, 'open')}
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Tenant signals detail */}
                    {signals.length > 0 && item.status !== 'complete' && (
                      <div style={{ padding: '0.625rem 1rem', backgroundColor: '#FEF2F2', borderTop: '1px solid #FECACA', fontSize: '0.8rem', color: '#7F1D1D' }}>
                        <strong>Tenant signals:</strong>{' '}
                        {signals.slice(0, 3).map((s, i) => (
                          <span key={i}>{s.category}{i < Math.min(signals.length, 3) - 1 ? ', ' : ''}</span>
                        ))}
                        {signals.length > 3 && ` +${signals.length - 3} more`}
                        {' '}— complaints match this system
                      </div>
                    )}

                    {/* Evidence upload panel */}
                    {isExpanded && (
                      <div style={{ padding: '1rem', backgroundColor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                        <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: '600' }}>Upload Remediation Evidence</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => setEvidenceFile(e.target.files[0])}
                            style={{ fontSize: '0.8rem' }}
                          />
                          <textarea
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '64px' }}
                            placeholder="Notes (contractor, invoice #, work description...)"
                            value={evidenceNotes}
                            onChange={e => setEvidenceNotes(e.target.value)}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <button style={btnSecondary} onClick={() => setExpandedItem(null)}>Cancel</button>
                            <button style={btnPrimary} onClick={() => handleEvidenceUpload(item.id)} disabled={savingEvidence}>
                              {savingEvidence ? 'Saving...' : 'Save Evidence'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary totals */}
          {immediateItems.length > 0 && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#F8FAFC', borderRadius: '0.5rem', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#DC2626' }}>{openImmediateCount}</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>Open Items</p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>
                    {immediateItems.filter(i => i.status === 'complete').length}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>Complete</p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>
                    {fmtCurrency(totalCostOpen)}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>Outstanding $</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* CAPITAL RESERVES TAB                                              */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'reserves' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>Capital Reserve Schedule</h3>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.2rem 0 0' }}>
                Major system replacements — Year 1 through Year 12
              </p>
            </div>
            {accountType !== 'lender' && (
              <button style={btnPrimary} onClick={() => { setNewItemPriority('capital_reserve'); setShowAddItem(true); }}>
                <Plus size={14} style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                Add Reserve Item
              </button>
            )}
          </div>

          {/* Add Reserve Item Form */}
          {showAddItem && newItemPriority === 'capital_reserve' && (
            <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.5rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.925rem', fontWeight: '700' }}>New Capital Reserve Item</h4>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowAddItem(false)}>
                  <X size={16} color="#94a3b8" />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>System Category</label>
                  <select style={inputStyle} value={newItemCategory} onChange={e => setNewItemCategory(e.target.value)}>
                    {PCA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Year Due *</label>
                  <input style={inputStyle} type="number" value={newItemYear} onChange={e => setNewItemYear(e.target.value)} placeholder="e.g. 2027" min="2024" max="2040" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Description *</label>
                  <input style={inputStyle} value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} placeholder="e.g. Full roof replacement" />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Estimated Cost ($)</label>
                  <input style={inputStyle} type="number" value={newItemCost} onChange={e => setNewItemCost(e.target.value)} placeholder="85000" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button style={btnSecondary} onClick={() => setShowAddItem(false)}>Cancel</button>
                <button style={btnPrimary} onClick={handleSaveItem} disabled={savingItem}>
                  {savingItem ? 'Saving...' : 'Save Item'}
                </button>
              </div>
            </div>
          )}

          {reserveItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <Clock size={32} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
              <p style={{ margin: 0, fontWeight: '500' }}>No capital reserve items yet</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
                Add the 12-year replacement schedule from your PCA report
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600', color: '#475569' }}>System / Item</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600', color: '#475569' }}>Category</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600', color: '#475569' }}>Estimated Cost</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600', color: '#475569' }}>Year Due</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600', color: '#475569' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reserveItems
                      .sort((a, b) => (a.yearDue || 9999) - (b.yearDue || 9999))
                      .map(item => {
                        const signals = getTenantSignals(item.category, building.reviews || []);
                        return (
                          <tr key={item.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                            <td style={{ padding: '0.75rem', color: '#0f172a', fontWeight: '500' }}>
                              {item.description}
                              {signals.length > 0 && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', backgroundColor: '#FEF2F2', color: '#DC2626', padding: '0.1rem 0.4rem', borderRadius: '9999px' }}>
                                  ⚡ {signals.length} signals
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem', color: '#64748b' }}>{item.category}</td>
                            <td style={{ padding: '0.75rem', color: '#0f172a', fontWeight: '500', textAlign: 'right' }}>
                              {fmtCurrency(item.estimatedCost)}
                            </td>
                            <td style={{ padding: '0.75rem', color: '#475569', textAlign: 'center' }}>
                              {item.yearDue || '—'}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              <StatusBadge status={item.status} />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                      <td colSpan={2} style={{ padding: '0.75rem', fontWeight: '700', color: '#0f172a' }}>Total Reserve Needs</td>
                      <td style={{ padding: '0.75rem', fontWeight: '700', color: '#0f172a', textAlign: 'right' }}>
                        {fmtCurrency(reserveItems.reduce((s, i) => s + (i.estimatedCost || 0), 0))}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* REPORTS ON FILE TAB                                               */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'reports' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>Third-Party Reports</h3>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.2rem 0 0' }}>
                PCA, Phase I/II, Appraisal, Zoning, and ALTA reports on file
              </p>
            </div>
            <button style={btnPrimary} onClick={() => setShowAddReport(!showAddReport)}>
              <Plus size={14} style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
              Upload Report
            </button>
          </div>

          {/* Upload form */}
          {showAddReport && (
            <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.5rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.925rem', fontWeight: '700' }}>Upload Report</h4>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowAddReport(false)}>
                  <X size={16} color="#94a3b8" />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Report Type *</label>
                  <select style={inputStyle} value={reportType} onChange={e => setReportType(e.target.value)}>
                    {REPORT_TYPES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Firm / Vendor</label>
                  <input style={inputStyle} value={reportFirm} onChange={e => setReportFirm(e.target.value)} placeholder="e.g. Criterium Engineers" />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Report Date *</label>
                  <input style={inputStyle} type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} />
                </div>

                {/* Loan info — only for PCA uploads */}
                {reportType === 'PCA' && (
                  <>
                    <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem', borderTop: '1px solid #E2E8F0', paddingTop: '0.75rem' }}>
                      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Loan Information (optional)</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Lender Name</label>
                      <input style={inputStyle} value={lenderName} onChange={e => setLenderName(e.target.value)} placeholder="e.g. Eagle Bank" />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Loan Amount ($)</label>
                      <input style={inputStyle} type="number" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} placeholder="2400000" />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Origination Date</label>
                      <input style={inputStyle} type="date" value={originationDate} onChange={e => setOriginationDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Maturity Date</label>
                      <input style={inputStyle} type="date" value={maturityDate} onChange={e => setMaturityDate(e.target.value)} />
                    </div>
                  </>
                )}

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '0.25rem' }}>Upload PDF *</label>
                  <input type="file" accept=".pdf" onChange={e => setReportFile(e.target.files[0])} style={{ fontSize: '0.875rem' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button style={btnSecondary} onClick={() => setShowAddReport(false)}>Cancel</button>
                <button style={btnPrimary} onClick={handleUploadReport} disabled={uploadingReport}>
                  {uploadingReport ? 'Uploading...' : 'Upload Report'}
                </button>
              </div>
            </div>
          )}

          {/* Reports list */}
          {reports.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <FileText size={32} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
              <p style={{ margin: 0, fontWeight: '500' }}>No reports uploaded yet</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>Upload the PCA, Phase I, Appraisal, or other third-party reports for this property</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {reports.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid #E2E8F0', borderRadius: '0.5rem', backgroundColor: 'white' }}>
                  <div style={{ width: 40, height: 40, backgroundColor: '#EFF6FF', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={18} color="#3B82F6" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '700', color: '#0f172a' }}>
                      {REPORT_TYPES.find(t => t.key === r.reportType)?.label || r.reportType}
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                      {r.reportFirm && `${r.reportFirm} · `}{r.reportDate}
                      {r.loanInfo?.lenderName && ` · ${r.loanInfo.lenderName}`}
                    </p>
                    {r.loanInfo?.loanAmount > 0 && (
                      <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                        Loan: {fmtCurrency(r.loanInfo.loanAmount)}
                        {r.loanInfo.originationDate && ` · Originated ${r.loanInfo.originationDate}`}
                        {r.loanInfo.maturityDate && ` · Matures ${r.loanInfo.maturityDate}`}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <a href={r.pdfUrl} target="_blank" rel="noopener noreferrer"
                      style={{ ...btnSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.75rem' }}>
                      <Download size={13} /> View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* TENANT SIGNALS TAB                                                */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'signals' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: '0 0 0.25rem' }}>Tenant Complaint → PCA Cross-Reference</h3>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
              Active tenant complaints that overlap with open PCA repair items
            </p>
          </div>

          {items.filter(i => i.status !== 'complete').length === 0 || (building.reviews || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              <CheckCircle size={32} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
              <p style={{ margin: 0, fontWeight: '500' }}>
                {(building.reviews || []).length === 0
                  ? 'No tenant reports for this property yet'
                  : 'No signal overlaps with open PCA items'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {items
                .filter(i => i.status !== 'complete')
                .map(item => {
                  const signals = getTenantSignals(item.category, building.reviews || []);
                  if (signals.length === 0) return null;
                  return (
                    <div key={item.id} style={{ border: '1px solid #FECACA', borderRadius: '0.5rem', overflow: 'hidden' }}>
                      <div style={{ padding: '0.875rem 1rem', backgroundColor: '#FEF2F2', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertTriangle size={14} color="#DC2626" />
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#DC2626' }}>
                          {signals.length} tenant complaint{signals.length !== 1 ? 's' : ''} match open PCA item
                        </span>
                      </div>
                      <div style={{ padding: '0.875rem 1rem', backgroundColor: 'white' }}>
                        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#0f172a' }}>
                          PCA Item: {item.description}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          {signals.slice(0, 5).map((s, i) => (
                            <div key={i} style={{ fontSize: '0.8rem', color: '#475569', padding: '0.4rem 0.6rem', backgroundColor: '#FFF7F7', borderRadius: '0.25rem', borderLeft: '3px solid #FCA5A5' }}>
                              <strong>{s.category}</strong>
                              {s.description && ` — "${s.description.slice(0, 80)}${s.description.length > 80 ? '...' : ''}"`}
                            </div>
                          ))}
                          {signals.length > 5 && (
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                              +{signals.length - 5} more complaints
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              {items.filter(i => i.status !== 'complete').every(i => getTenantSignals(i.category, building.reviews || []).length === 0) && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  <CheckCircle size={28} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
                  <p style={{ margin: 0 }}>No overlapping signals found</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
